import React, { useEffect, useState } from "react";
import {
  Switch,
  Spin,
  Modal,
  Button,
  Input,
  Select,
  Space,
  Typography,
  Tag,
  Form,
  Alert,
  Divider,
} from "antd";
import {
  ApiOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DisconnectOutlined,
  LoadingOutlined,
  SettingOutlined,
  ThunderboltOutlined,
  SyncOutlined,
  LinkedinOutlined,
  CloudOutlined,
  FileProtectOutlined,
} from "@ant-design/icons";
import { INTEGRATION_KEYS } from "../../constants/recruitment.constants";
import { useSelector, useDispatch } from "react-redux";
import type { AppDispatch } from "../../../../core/data/redux/store";
import {
  fetchIntegrations,
  toggleIntegration,
  testIntegrationConnection,
  updateIntegrationCredentials,
  updateSyncFrequency,
} from "../../../../core/data/redux/recruitment/thunks";
import {
  selectIntegrations,
  selectIntegrationsLoading,
} from "../../../../core/data/redux/recruitment/selectors";
import { notify } from "../../../../services/toast.config";

const { Text, Title, Paragraph } = Typography;

const PLATFORM_META: Record<
  string,
  { icon: React.ReactNode; color: string; bg: string; description: string }
> = {
  linkedin: {
    icon: <LinkedinOutlined style={{ fontSize: 22, color: "#0A66C2" }} />,
    color: "#0A66C2",
    bg: "#E8F0FC",
    description: "Post jobs directly to LinkedIn and import applicants automatically.",
  },
  indeed: {
    icon: <CloudOutlined style={{ fontSize: 22, color: "#2164F3" }} />,
    color: "#2164F3",
    bg: "#EAF0FF",
    description: "Sync open positions to Indeed and pull candidate applications in real time.",
  },
  naukri: {
    icon: <i className="ti ti-building" style={{ fontSize: 22, color: "#3B82F6" }} />,
    color: "#3B82F6",
    bg: "#EFF6FF",
    description: "Publish job listings on Naukri and manage inbound applications from the portal.",
  },
  resume_parser: {
    icon: <FileProtectOutlined style={{ fontSize: 22, color: "#7C3AED" }} />,
    color: "#7C3AED",
    bg: "#F3EEFF",
    description: "Automatically parse uploaded resumes and populate candidate profiles using RChilli.",
  },
  openai: {
    icon: <ThunderboltOutlined style={{ fontSize: 22, color: "#10A37F" }} />,
    color: "#10A37F",
    bg: "#F0FCF9",
    description: "Use GPT-4o for advanced resume parsing, ATS scoring, and candidate summaries.",
  },
  gemini: {
    icon: <CloudOutlined style={{ fontSize: 22, color: "#4285F4" }} />,
    color: "#4285F4",
    bg: "#F0F7FF",
    description: "Leverage Google Gemini Pro for multimodal resume analysis and matching.",
  },
  anthropic: {
    icon: <SettingOutlined style={{ fontSize: 22, color: "#D97757" }} />,
    color: "#D97757",
    bg: "#FEF7F5",
    description: "Use Claude 3.5 Sonnet for highly accurate extraction of candidate experience.",
  },
};

const SYNC_OPTIONS = [
  { value: 15, label: "Every 15 min" },
  { value: 30, label: "Every 30 min" },
  { value: 60, label: "Every 1 hr" },
  { value: 120, label: "Every 2 hrs" },
  { value: 360, label: "Every 6 hrs" },
  { value: 720, label: "Every 12 hrs" },
  { value: 1440, label: "Every 24 hrs" },
];


const IntegrationSettings: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const integrations = useSelector(selectIntegrations);
  const loading = useSelector(selectIntegrationsLoading);
  const activeCompany = useSelector((state: any) => state.recruitment.activeCompany);

  const [testingKey, setTestingKey] = useState<string | null>(null);
  const [configuringKey, setConfiguringKey] = useState<string | null>(null);
  const [savingConfig, setSavingConfig] = useState(false);
  const [configForm, setConfigForm] = useState({
    api_key: "", api_secret: "", access_token: "", sync_frequency_minutes: 60,
  });

  useEffect(() => {
    if (activeCompany?.company_id) dispatch(fetchIntegrations());
  }, [dispatch, activeCompany?.company_id, activeCompany?.company_type]);

  const handleToggle = async (key: string, enabled: boolean) => {
    try {
      await dispatch(toggleIntegration({ integrationKey: key, enabled })).unwrap();
      dispatch(fetchIntegrations());
      notify.success(`${enabled ? "Enabled" : "Disabled"} successfully`);
    } catch (err: any) {
      notify.error(err?.data?.message || err || "Toggle failed");
    }
  };

  const handleTest = async (key: string) => {
    setTestingKey(key);
    try {
      await dispatch(testIntegrationConnection(key)).unwrap();
      dispatch(fetchIntegrations());
      notify.success("Connection successful!");
    } catch (err: any) {
      notify.error(err?.data?.message || err || "Connection failed");
      dispatch(fetchIntegrations());
    } finally {
      setTestingKey(null);
    }
  };

  const handleOpenConfig = (key: string) => {
    const cfg = integrations.find((i) => i.key === key);
    setConfigForm({
      api_key: cfg?.api_key || "",
      api_secret: cfg?.api_secret || "",
      access_token: cfg?.access_token || "",
      sync_frequency_minutes: cfg?.sync_frequency_minutes || 60,
    });
    setConfiguringKey(key);
  };

  const handleSaveConfig = async () => {
    if (!activeCompany || !configuringKey) return;
    setSavingConfig(true);
    try {
      await dispatch(updateIntegrationCredentials({
        integrationKey: configuringKey,
        apiKey: configForm.api_key,
        apiSecret: configForm.api_secret,
        accessToken: configForm.access_token,
      })).unwrap();
      await dispatch(updateSyncFrequency({
        integrationKey: configuringKey,
        syncFrequencyMinutes: configForm.sync_frequency_minutes,
      })).unwrap();
      notify.success("Configuration saved!");
      dispatch(fetchIntegrations());
      setConfiguringKey(null);
    } catch (err: any) {
      notify.error(err?.data?.message || err || "Failed to save");
    } finally {
      setSavingConfig(false);
    }
  };

  const statusTag = (status: string) => {
    if (status === "connected")
      return <Tag icon={<CheckCircleOutlined />} color="success">Connected</Tag>;
    if (status === "error")
      return <Tag icon={<CloseCircleOutlined />} color="error">Error</Tag>;
    return <Tag icon={<DisconnectOutlined />} color="default">Disconnected</Tag>;
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "80px 0" }}>
        <Spin size="large" />
      </div>
    );
  }

  const connected = integrations.filter((i) => i.connection_status === "connected").length;

  return (
    <>
      
      <div style={{ marginBottom: 24 }}>
        <Title level={5} style={{ margin: 0, fontWeight: 600 }}>
          Platform Integrations
        </Title>
        <Paragraph type="secondary" style={{ margin: "4px 0 0", fontSize: 13 }}>
          Connect external job boards and tools to automate your hiring pipeline.
          {connected > 0 && (
            <Text style={{ marginLeft: 8, color: "#52c41a", fontWeight: 500 }}>
              {connected} of {INTEGRATION_KEYS.length} connected
            </Text>
          )}
        </Paragraph>
      </div>

      
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(380px, 1fr))",
          gap: 16,
        }}>
        {INTEGRATION_KEYS.map(({ key, label }) => {
          const config = integrations.find((i) => i.key === key);
          const status = config?.connection_status || "disconnected";
          const meta = PLATFORM_META[key] ?? {
            icon: <ApiOutlined style={{ fontSize: 22 }} />,
            color: "#8c8c8c", bg: "#f5f5f5",
            description: "Connect this platform to your recruitment workflow.",
          };
          const isTesting = testingKey === key;

          return (
            <div
              key={key}
              style={{
                background: "#fff",
                border: status === "connected"
                  ? "1px solid #b7eb8f"
                  : "1px solid #f0f0f0",
                borderRadius: 10,
                padding: 20,
                display: "flex",
                flexDirection: "column",
                gap: 0,
                transition: "box-shadow 0.2s",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.boxShadow = "0 2px 12px rgba(0,0,0,0.08)")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.boxShadow = "none")
              }>

              
              <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
                <div
                  style={{
                    width: 44, height: 44, borderRadius: 10,
                    background: meta.bg,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    flexShrink: 0,
                  }}>
                  {meta.icon}
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Text strong style={{ fontSize: 14 }}>{label}</Text>
                    <Switch
                      checked={config?.enabled || false}
                      size="small"
                      onChange={(checked) => handleToggle(key, checked)}
                    />
                  </div>
                  <div style={{ marginTop: 4 }}>{statusTag(status)}</div>
                </div>
              </div>

              
              <Text
                type="secondary"
                style={{ fontSize: 12.5, lineHeight: 1.6, marginTop: 12, display: "block" }}>
                {meta.description}
              </Text>

              <Divider style={{ margin: "14px 0" }} />

              
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Text type="secondary" style={{ fontSize: 11.5 }}>
                  {config?.last_synced ? (
                    <><SyncOutlined style={{ marginRight: 4 }} />
                      Last synced {new Date(config.last_synced).toLocaleDateString()}
                    </>
                  ) : (
                    "Never synced"
                  )}
                </Text>

                <Space size={6}>
                  <Button
                    size="small"
                    icon={isTesting ? <LoadingOutlined /> : <ThunderboltOutlined />}
                    onClick={() => handleTest(key)}
                    loading={isTesting}>
                    Test
                  </Button>
                  <Button
                    size="small"
                    icon={<SettingOutlined />}
                    type="primary"
                    ghost
                    onClick={() => handleOpenConfig(key)}>
                    Configure
                  </Button>
                </Space>
              </div>
            </div>
          );
        })}
      </div>

      
      <Modal
        title={
          <Space>
            <SettingOutlined />
            Configure {INTEGRATION_KEYS.find((i) => i.key === configuringKey)?.label}
          </Space>
        }
        open={!!configuringKey}
        onCancel={() => !savingConfig && setConfiguringKey(null)}
        onOk={handleSaveConfig}
        confirmLoading={savingConfig}
        okText="Save Configuration"
        centered
        destroyOnClose
        maskClosable={false}
        keyboard={false}
        zIndex={1001}
        getContainer={() => document.body}
        styles={{
          content: {
            padding: 0, display: "flex", flexDirection: "column",
            maxHeight: "90vh", overflow: "hidden", borderRadius: 12,
          },
          header: {
            flexShrink: 0, padding: "16px 24px 12px",
            borderBottom: "1px solid #f0f0f0", marginBottom: 0,
          },
          body: {
            flex: 1, overflowY: "auto", overflowX: "hidden",
            padding: "20px 24px 8px",
            scrollbarWidth: "thin" as any, scrollbarColor: "#d1d5db transparent",
          },
          footer: {
            flexShrink: 0, padding: "12px 24px 16px",
            borderTop: "1px solid #f0f0f0", marginTop: 0,
          },
          mask: {
            position: "fixed", inset: 0, width: "100vw", height: "100vh",
            backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)",
            background: "rgba(0, 0, 0, 0.45)",
          },
        }}
      >
        <Form layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item label="API Key">
            <Input
              placeholder="Enter API Key"
              value={configForm.api_key}
              onChange={(e) => setConfigForm((p) => ({ ...p, api_key: e.target.value }))}
            />
          </Form.Item>
          {configuringKey && !["openai", "gemini", "anthropic"].includes(configuringKey) && (
            <>
              <Form.Item label="API Secret (Optional)">
                <Input.Password
                  placeholder="Enter API Secret"
                  value={configForm.api_secret}
                  onChange={(e) => setConfigForm((p) => ({ ...p, api_secret: e.target.value }))}
                />
              </Form.Item>
              {configuringKey !== "resume_parser" && (
                <Form.Item label="Access Token (Alternative)">
                  <Input.Password
                    placeholder="Enter Access Token"
                    value={configForm.access_token}
                    onChange={(e) => setConfigForm((p) => ({ ...p, access_token: e.target.value }))}
                  />
                  <Alert
                    type="info"
                    showIcon
                    banner
                    message="Provide either an API Key (+ Secret) or an Access Token depending on the platform."
                    style={{ marginTop: 8 }}
                  />
                </Form.Item>
              )}
              {configuringKey !== "resume_parser" && (
                <Form.Item label="Sync Frequency">
                  <Select
                    value={configForm.sync_frequency_minutes}
                    onChange={(val) => setConfigForm((p) => ({ ...p, sync_frequency_minutes: val }))}
                    options={SYNC_OPTIONS}
                  />
                </Form.Item>
              )}
            </>
          )}
          {configuringKey && ["openai", "gemini", "anthropic"].includes(configuringKey) && (
            <Alert
              type="info"
              showIcon
              message="Only an API Key is required for AI providers. Enter your key above and click Test to verify the connection."
              style={{ marginTop: 8 }}
            />
          )}
        </Form>
      </Modal>
    </>
  );
};

export default IntegrationSettings;
